import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, ErrorOutline, Place, Tune } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useMyLocations } from '../../hooks/useMyLocations';
import { isStudioScopedRole } from '../../utils/roles';
import { useAuth } from '../../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
//  Shared pieces for "create at several locations" and "import from a location".
// ─────────────────────────────────────────────────────────────────────────────

export interface BrandLocation {
  _id: string;
  name: string;
  studioName: string;
  isActive: boolean;
  editable: boolean;
}

/** One problem the server found, named by location (see multi-location.service). */
export interface LocationProblem {
  studioId: string;
  location: string;
  index?: number;
  field?: string;
  message: string;
}

export const ACCENT = '#e96d9a';
export const ACCENT_TINT = '#fdf0f5';
export const OK = '#2e7d32';
export const BAD = '#c62828';

/**
 * The brand's locations where this account may create (manager or above),
 * found from any one of them. For a studio role with no anchor the account's
 * own studio is used.
 */
export function useBrandLocations(anchorStudioId?: string | null, enabled = true) {
  const { admin } = useAuth();
  const isStudio = isStudioScopedRole(admin?.role);
  const { locations: mine } = useMyLocations();
  // `enabled` false: no request at all — a form that does not offer the
  // picker (a closed dialog, an edit, a single-location studio's flow) must
  // not cost a round trip.
  const anchor = enabled ? anchorStudioId || (isStudio ? (admin as any)?.studio?._id : '') || '' : '';
  const [all, setAll] = useState<BrandLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!anchor) { setAll([]); return; }
    let live = true;
    setLoading(true);
    setError(null);
    apiService
      .getBrandLocationsForStudio(anchor)
      .then((res: any) => {
        if (!live) return;
        const list: BrandLocation[] = (res?.locations ?? []).map((l: any) => ({ ...l, _id: String(l._id) }));
        setAll(list);
      })
      .catch((e: any) => live && setError(e?.data?.message || e?.message || 'Could not load the locations'))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [anchor]);

  // A studio role can only read locations it holds, even ones it may not edit.
  const held = new Set(mine.map((l) => String(l._id)));
  const readable = isStudio ? all.filter((l) => held.has(l._id)) : all;
  const creatable = all.filter((l) => l.editable && l.isActive);
  return { anchor, all, readable, creatable, loading, error };
}

export interface LocationData {
  classes: any[];
  instructors: any[];
  rooms: any[];
  enableSessionTypes: boolean;
}

/**
 * Loads each location's own classes, instructors and rooms once, on demand.
 * Instructors and rooms belong to one location, so every tab reads its own.
 */
export function useLocationData() {
  const [data, setData] = useState<Record<string, LocationData | 'loading' | 'error'>>({});
  const asked = useRef<Set<string>>(new Set());

  const load = useCallback((ids: string[], force = false) => {
    ids.forEach((id) => {
      if (!id || (!force && asked.current.has(id))) return;
      asked.current.add(id);
      setData((d) => ({ ...d, [id]: 'loading' }));
      Promise.all([
        apiService.getStudioClasses(id).catch(() => ({ classes: [] })),
        apiService.getInstructorsByStudio(id, {}).catch(() => ({ instructors: [] })),
        // A 403 just means seat selection is off at this location.
        apiService.getSeatLayouts(id).catch(() => ({ seatLayouts: [] })),
        apiService.getStudioById(id).catch(() => null),
      ])
        .then(([c, i, r, s]: any[]) => {
          const studio = s?.studio || s;
          setData((d) => ({
            ...d,
            [id]: {
              classes: c?.classes || [],
              instructors: (i?.instructors || []).filter((x: any) => !x.isDisabled),
              rooms: r?.seatLayouts || r?.data?.seatLayouts || [],
              enableSessionTypes: !!studio?.studioPermissions?.enableSessionTypes,
            },
          }));
        })
        .catch(() => setData((d) => ({ ...d, [id]: 'error' })));
    });
  }, []);

  const get = (id: string): LocationData | null => {
    const v = data[id];
    return v && typeof v === 'object' ? v : null;
  };
  const isLoading = (ids: string[]) => ids.some((id) => data[id] === 'loading' || data[id] === undefined);
  return { load, get, isLoading };
}

/** The server's "fix these first" answer, or null for any other error. */
export function problemsOf(err: any): LocationProblem[] | null {
  const data = err?.data ?? err?.response?.data;
  return data?.error === 'LOCATION_PROBLEMS' && Array.isArray(data.problems) ? data.problems : null;
}

export function messageOf(err: any, fallback = 'Something went wrong. Please try again.'): string {
  const m = err?.data?.message ?? err?.response?.data?.message ?? err?.message;
  return Array.isArray(m) ? m.join(' ') : m || fallback;
}

export const idOf = (v: any): string => String(v?._id ?? v ?? '');

/** Does this instructor teach at this location? */
export function teachesAt(ins: any, studioId: string): boolean {
  const at = (ins?.studios?.length ? ins.studios : [ins?.studio]).filter(Boolean).map(idOf);
  return at.includes(String(studioId));
}

/**
 * The same class at another location: a linked copy first, then the same
 * name. Returns null when that location has no such class.
 */
export function matchClass(source: any, candidates: any[]): any | null {
  if (!source) return null;
  const group = source.siblingGroup ? String(source.siblingGroup) : '';
  if (group) {
    const linked = candidates.find((c) => c.siblingGroup && String(c.siblingGroup) === group);
    if (linked) return linked;
  }
  const name = String(source.className ?? '').trim().toLowerCase();
  return candidates.find((c) => String(c.className ?? '').trim().toLowerCase() === name) || null;
}

// ── UI ──────────────────────────────────────────────────────────────────────

export const StepHeader: React.FC<{ steps: string[]; active: number }> = ({ steps, active }) => (
  <Stepper activeStep={active} alternativeLabel sx={{ mb: 3, '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: ACCENT } }}>
    {steps.map((s) => (
      <Step key={s}><StepLabel>{s}</StepLabel></Step>
    ))}
  </Stepper>
);

/** Location cards with a checkbox each. */
export const LocationPicker: React.FC<{
  locations: BrandLocation[];
  selected: string[];
  onChange: (ids: string[]) => void;
  disabledIds?: string[];
  disabledReason?: string;
  helper?: React.ReactNode;
}> = ({ locations, selected, onChange, disabledIds = [], disabledReason, helper }) => {
  const choosable = locations.filter((l) => !disabledIds.includes(l._id));
  const allOn = choosable.length > 0 && choosable.every((l) => selected.includes(l._id));
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {selected.length ? `${selected.length} location${selected.length === 1 ? '' : 's'} selected` : 'Choose at least one location'}
        </Typography>
        {choosable.length > 1 && (
          <Button size="small" onClick={() => onChange(allOn ? [] : choosable.map((l) => l._id))}>
            {allOn ? 'Clear all' : 'Select all'}
          </Button>
        )}
      </Box>
      <Box display="grid" gap={1.5} sx={{ gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
        {locations.map((l) => {
          const on = selected.includes(l._id);
          const off = disabledIds.includes(l._id);
          return (
            <Tooltip key={l._id} title={off ? disabledReason ?? '' : ''}>
              <Box
                role="checkbox"
                aria-checked={on}
                aria-disabled={off}
                tabIndex={off ? -1 : 0}
                onClick={() => !off && toggle(l._id)}
                onKeyDown={(e) => { if (!off && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); toggle(l._id); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 2,
                  border: '1.5px solid', borderColor: on ? ACCENT : 'divider',
                  bgcolor: on ? ACCENT_TINT : 'background.paper',
                  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1,
                  transition: 'border-color .15s, background-color .15s',
                  '&:hover': off ? {} : { borderColor: ACCENT },
                  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                }}
              >
                <Checkbox checked={on} disabled={off} size="small" sx={{ p: 0, '&.Mui-checked': { color: ACCENT } }} tabIndex={-1} />
                <Place sx={{ color: on ? ACCENT : 'text.disabled', fontSize: 20 }} />
                <Box minWidth={0}>
                  <Typography fontWeight={600} noWrap>{l.name}</Typography>
                  {l.studioName && l.studioName !== l.name && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">{l.studioName}</Typography>
                  )}
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {helper && <Box mt={2}>{helper}</Box>}
    </Box>
  );
};

/** Tabs: an optional "All locations" tab, then one per location with a ready/needs-work mark. */
export const LocationTabs: React.FC<{
  value: string;
  onChange: (v: string) => void;
  locations: BrandLocation[];
  issues: Record<string, number>;
  customised?: Record<string, number>;
  withAllTab?: boolean;
}> = ({ value, onChange, locations, issues, customised = {}, withAllTab = true }) => (
  <Tabs
    value={value}
    onChange={(_e, v) => onChange(v)}
    variant="scrollable"
    scrollButtons="auto"
    sx={{
      borderBottom: 1, borderColor: 'divider', mb: 2,
      '& .MuiTab-root': { textTransform: 'none', minHeight: 48, fontWeight: 600 },
      '& .Mui-selected': { color: `${ACCENT} !important` },
      '& .MuiTabs-indicator': { backgroundColor: ACCENT },
    }}
  >
    {withAllTab && <Tab value="__all" icon={<Tune fontSize="small" />} iconPosition="start" label="All locations" />}
    {locations.map((l) => {
      const n = issues[l._id] ?? 0;
      const c = customised[l._id] ?? 0;
      return (
        <Tab
          key={l._id}
          value={l._id}
          iconPosition="start"
          icon={n ? <ErrorOutline fontSize="small" sx={{ color: BAD }} /> : <CheckCircle fontSize="small" sx={{ color: OK }} />}
          label={
            <Box display="flex" alignItems="center" gap={0.75}>
              {l.name}
              {n > 0 && <Chip size="small" label={`${n} to fix`} sx={{ height: 18, fontSize: 11, bgcolor: '#fdecea', color: BAD }} />}
              {!n && c > 0 && <Chip size="small" label={`${c} changed`} sx={{ height: 18, fontSize: 11, bgcolor: ACCENT_TINT, color: ACCENT }} />}
            </Box>
          }
        />
      );
    })}
  </Tabs>
);

/** "Uses All locations" / "Changed here · Reset" under a field in a location tab. */
export const FieldOrigin: React.FC<{ custom: boolean; onReset?: () => void; inherited?: string }> = ({ custom, onReset, inherited }) =>
  custom ? (
    <Box component="span" sx={{ color: ACCENT, fontWeight: 600 }}>
      Changed for this location{onReset && (<> · <Box component="button" type="button" onClick={onReset} sx={{ border: 0, background: 'none', color: ACCENT, cursor: 'pointer', p: 0, font: 'inherit', textDecoration: 'underline' }}>Use the All locations value</Box></>)}
    </Box>
  ) : (
    <Box component="span">{inherited ?? 'Same as All locations'}</Box>
  );

/** Spinner while a location's dropdown data loads. */
export const Loading: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <Box display="flex" alignItems="center" gap={1.5} py={4} justifyContent="center" color="text.secondary">
    <CircularProgress size={20} sx={{ color: ACCENT }} /> {label}
  </Box>
);

/** What happened, per location, after creating. */
export const ResultPanel: React.FC<{
  created: { location: string; label: string }[];
  failed: { location: string; message: string }[];
}> = ({ created, failed }) => (
  <Box display="flex" flexDirection="column" gap={1.5}>
    {failed.length === 0 ? (
      <Alert severity="success" icon={<CheckCircle />}>Everything was created.</Alert>
    ) : (
      <Alert severity="warning">
        Some locations could not be completed. The ones marked done were created; try the others again.
      </Alert>
    )}
    {created.map((c, i) => (
      <Box key={`c${i}`} display="flex" alignItems="center" gap={1.25} p={1.25} borderRadius={2} bgcolor="#f1f8f1">
        <CheckCircle sx={{ color: OK }} fontSize="small" />
        <Typography fontWeight={600} minWidth={120}>{c.location}</Typography>
        <Typography variant="body2" color="text.secondary">{c.label}</Typography>
      </Box>
    ))}
    {failed.map((f, i) => (
      <Box key={`f${i}`} display="flex" alignItems="center" gap={1.25} p={1.25} borderRadius={2} bgcolor="#fdecea">
        <ErrorOutline sx={{ color: BAD }} fontSize="small" />
        <Typography fontWeight={600} minWidth={120}>{f.location}</Typography>
        <Typography variant="body2">{f.message}</Typography>
      </Box>
    ))}
  </Box>
);

/** The server's list of problems, grouped by location, each with a "Go to tab" link. */
export const ProblemsPanel: React.FC<{ problems: LocationProblem[]; onGo?: (studioId: string) => void }> = ({ problems, onGo }) => {
  const byLoc = new Map<string, LocationProblem[]>();
  problems.forEach((p) => byLoc.set(p.studioId, [...(byLoc.get(p.studioId) ?? []), p]));
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <Typography fontWeight={700} mb={0.5}>Nothing was created. Fix these first:</Typography>
      {Array.from(byLoc.entries()).map(([sid, list]) => (
        <Box key={sid} mb={0.75}>
          <Typography variant="body2" fontWeight={600}>
            {list[0].location}
            {onGo && (
              <Button size="small" onClick={() => onGo(sid)} sx={{ ml: 1, py: 0, minWidth: 0, textTransform: 'none' }}>Go to tab</Button>
            )}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {list.map((p, i) => <li key={i}><Typography variant="body2">{p.message}</Typography></li>)}
          </Box>
        </Box>
      ))}
    </Alert>
  );
};

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "2026-10-14" + "07:30" → the IST timestamp the session API expects. */
export const istDate = (dateKey: string, time: string) => `${dateKey}T${time}:00.000+05:30`;

/** A Date → { dateKey, time } in IST. */
export function toIst(d: Date | string): { dateKey: string; time: string } {
  const x = new Date(d);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(x);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return { dateKey: `${get('year')}-${get('month')}-${get('day')}`, time: `${hour}:${get('minute')}` };
}

export const fmtDay = (dateKey: string) =>
  new Date(`${dateKey}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

export function addDays(dateKey: string, n: number): string {
  const d = new Date(`${dateKey}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const todayKey = () => toIst(new Date()).dateKey;

/**
 * Should the "several locations" flows be offered? Only when this account can
 * create at two or more locations of the brand — a single-location studio
 * keeps exactly the dialogs it has today.
 */
export function useMultiLocationAvailable(anchorStudioId?: string | null): boolean {
  const { creatable } = useBrandLocations(anchorStudioId);
  return creatable.length >= 2;
}

/**
 * "Create at" — a compact row of location checkboxes that sits at the top of
 * an ordinary create form.
 */
export const LocationChecks: React.FC<{
  locations: BrandLocation[];
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Overrides the line shown when several are ticked. */
  severalHint?: string;
  /** The heading; "Create at" unless the form says otherwise. */
  label?: string;
}> = ({ locations, selected, onChange, severalHint, label = 'Create at' }) => {
  const allOn = locations.length > 0 && locations.every((l) => selected.includes(l._id));
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <Box p={1.5} borderRadius={2} bgcolor={ACCENT_TINT} border={`1px solid ${ACCENT}33`}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75} gap={1}>
        <Typography fontWeight={700} fontSize={14}>{label} *</Typography>
        {locations.length > 1 && (
          <Button size="small" onClick={() => onChange(allOn ? [] : locations.map((l) => l._id))} sx={{ textTransform: 'none', color: ACCENT }}>
            {allOn ? 'Clear all' : 'All locations'}
          </Button>
        )}
      </Box>
      <Box display="flex" flexWrap="wrap" columnGap={2.5}>
        {locations.map((l) => (
          <Box key={l._id} component="label" display="flex" alignItems="center" sx={{ cursor: 'pointer' }}>
            <Checkbox size="small" checked={selected.includes(l._id)} onChange={() => toggle(l._id)} sx={{ pl: 0, '&.Mui-checked': { color: ACCENT } }} />
            <Typography variant="body2" fontWeight={selected.includes(l._id) ? 600 : 400}>{l.name}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {selected.length > 1
          ? severalHint ?? `Each location gets its own copy. After this you can check and change each one.`
          : selected.length === 1 ? 'Tick more locations to create a copy at each.' : 'Choose at least one location.'}
      </Typography>
    </Box>
  );
};

// ── "Create at" for single-record forms (packs, advertisements, coupons) ────

/**
 * The locations a create form may write to, and which are ticked. Offered
 * only on a create, and only when [anchorStudioId]'s brand has two or more
 * locations this account may create at — every other studio keeps its form
 * exactly as it is (`multi` false, nothing rendered).
 *
 * [initial] seeds the ticks each time the form opens (or the anchor's brand
 * changes); ids this account cannot create at are dropped.
 */
export function useCreateAtLocations(
  anchorStudioId: string | null | undefined,
  { enabled, initial }: { enabled: boolean; initial: string[] },
) {
  // A studio login holding one location never has anything to pick, and its
  // locations are already loaded — so it never even asks the server.
  const { admin } = useAuth();
  const { locations: mine } = useMyLocations();
  const studioLogin = isStudioScopedRole(admin?.role);
  const on = enabled && (!studioLogin || mine.length >= 2);
  const { creatable, loading } = useBrandLocations(anchorStudioId, on);
  const multi = on && creatable.length >= 2;
  const [selected, setSelected] = useState<string[]>([]);
  const key = creatable.map((l) => l._id).join(',');
  const initialKey = initial.join(',');
  useEffect(() => {
    if (!multi) { setSelected([]); return; }
    setSelected(initial.filter((id) => creatable.some((l) => l._id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, key, initialKey]);
  const nameOf = (id: string) => creatable.find((l) => l._id === id)?.name || 'this location';
  return { multi, creatable, selected, setSelected, loading, nameOf };
}

/**
 * Runs [create] once per location, one after another, and never stops at the
 * first failure: each location is its own record, so what succeeded stays.
 */
export async function createAtEach(
  ids: string[],
  nameOf: (id: string) => string,
  create: (studioId: string) => Promise<any>,
): Promise<{ created: string[]; failed: { studioId: string; location: string; message: string }[] }> {
  const created: string[] = [];
  const failed: { studioId: string; location: string; message: string }[] = [];
  for (const id of ids) {
    try {
      await create(id);
      created.push(id);
    } catch (e: any) {
      failed.push({ studioId: id, location: nameOf(id), message: messageOf(e) });
    }
  }
  return { created, failed };
}

/** "Created at A and B. Not created at C: reason." for a partial result. */
export function partialResultMessage(
  created: string[],
  failed: { location: string; message: string }[],
  nameOf: (id: string) => string,
): string {
  const done = created.length ? `Created at ${created.map(nameOf).join(', ')}. ` : '';
  return `${done}Not created at ${failed.map((f) => `${f.location} (${f.message})`).join('; ')}.`;
}
